package com.elan.edubridge.shared.data.datasource

import com.elan.edubridge.shared.domain.Cancellable
import com.elan.edubridge.shared.domain.model.AcademyApplication
import com.elan.edubridge.shared.domain.model.AcademyData
import com.elan.edubridge.shared.domain.model.Assignment
import com.elan.edubridge.shared.domain.model.AssignmentSubmission
import com.elan.edubridge.shared.domain.model.AttendanceEvent
import com.elan.edubridge.shared.domain.model.AttendanceSummary
import com.elan.edubridge.shared.domain.model.AttendanceTimelineEntry
import com.elan.edubridge.shared.domain.model.CalendarEvent
import com.elan.edubridge.shared.domain.model.ClassData
import com.elan.edubridge.shared.domain.model.ContentReport
import com.elan.edubridge.shared.domain.model.DailyRosterEntry
import com.elan.edubridge.shared.domain.model.GuardianLink
import com.elan.edubridge.shared.domain.model.JoinApplication
import com.elan.edubridge.shared.domain.model.JoinApplicationStatus
import com.elan.edubridge.shared.domain.model.NoticeData
import com.elan.edubridge.shared.domain.model.PostComment
import com.elan.edubridge.shared.domain.model.PostData
import com.elan.edubridge.shared.domain.model.ReadResult
import com.elan.edubridge.shared.domain.model.StudentData
import com.elan.edubridge.shared.domain.model.TodayClass

/**
 * 플랫폼 중립 데이터 경계. Firebase 타입은 절대 이 위로 올라오지 않는다(§9.2).
 *
 * **테넌시:** 모든 조회가 `academyId`(또는 `userId`)를 받는다. 인자 없는 조회는 멀티테넌시를
 * 표현할 수 없어 학원 A 화면에 학원 B 데이터가 섞이는 것을 타입으로 막지 못한다.
 *
 * **`serviceDate`가 nullable인 이유:** 오늘이 며칠인지는 학원 `timeZone`에 달렸고(R12)
 * 그 판정에 필요한 시계·학원 설정은 구현 쪽에 있다. null이면 "학원 timeZone 기준 오늘"이다.
 * domain이 시계를 들고 있지 않게 하려는 의도적 설계다.
 *
 * **실패 규약:** 복구 가능한 실패(권한 없음·네트워크)는 예외를 던지지 말고 빈 리스트를 반환한다.
 * Swift가 이 계약을 구현하고 Kotlin이 호출하므로 경계를 넘는 예외는 복구 지점이 없다.
 * 취소(`CancellationException`)만 통과시킨다. 쓰기 실패는 [AcademyCommandDataSource]가
 * 반환값으로 표현한다.
 *
 * **경계 메서드에 기본 인자를 두지 않는다** — Kotlin 기본값은 ObjC 헤더에 노출되지 않아
 * Swift 구현·호출부에서 사라진다.
 *
 * **소프트 삭제 문서는 걸러 내지 않는다.** 공지·게시글·댓글은 삭제돼도 `status: DELETED`
 * 툼스톤으로 남는데, 이 계층은 저장소가 준 것을 그대로 올려 보내고 ACTIVE 필터는
 * [com.elan.edubridge.shared.data.repository.EduConnectRepositoryImpl] 한 곳에서 건다 —
 * 구현이 셋(InMemory·Android·web, M3에 Swift 추가)이라 각자 걸게 하면 하나가 빠지는 날이 온다.
 */
interface EduConnectDataSource {
    /** 사용자가 ACTIVE 멤버십을 가진 학원들. 목록은 호출부가 세션에서 만들어 넘긴다. */
    suspend fun academies(academyIds: List<String>): List<AcademyData>

    suspend fun classes(academyId: String): List<ClassData>

    suspend fun students(academyId: String): List<StudentData>

    suspend fun notices(academyId: String): List<NoticeData>

    /**
     * 학원 게시판 글 목록. 게시판은 학원당 하나라 `boardId`가 없다.
     *
     * 첨부는 문서 안에 인라인돼 있어([PostData.attachments]) 목록을 그리는 데 추가 호출이 없다.
     */
    suspend fun posts(academyId: String): List<PostData>

    /** 글 하나. 문서가 없으면 null이다(권한 없음·네트워크 실패도 같다 — 실패 규약). */
    suspend fun post(academyId: String, postId: String): PostData?

    /** 한 게시글의 댓글. 공지에는 댓글 경로가 없으므로 [postId]는 반드시 게시글 id다. */
    suspend fun postComments(academyId: String, postId: String): List<PostComment>

    /**
     * 학원 캘린더의 한 구간. 캘린더는 학원당 하나라 `calendarId`가 없다.
     *
     * **구간은 `startAt` 한 필드로만 자른다** — `[start, end]` 양끝 포함이며, 값은
     * UTC 정규형 ISO-8601이어야 한다(서버가 그 형태로만 저장하므로 문자열 비교가 곧 시각
     * 비교다). Firestore는 서로 다른 두 필드에 범위 조건을 함께 걸 수 없어서
     * `endAt >= 구간 시작`을 함께 요구할 수 없다 — **구간보다 먼저 시작해 구간 안까지
     * 이어지는 일정은 나오지 않는다.** 한 달 화면은 앞뒤로 며칠 넉넉히 잡아 호출해야 한다.
     * 얼마나 넉넉히 잡을지는 화면이 아는 값이라 이 계층에서 정하지 않는다.
     */
    suspend fun academyEvents(
        academyId: String,
        startAt: String,
        endAt: String,
    ): List<CalendarEvent>

    /**
     * 개인 캘린더의 한 구간. 구간 규칙은 [academyEvents]와 같다.
     *
     * 격리가 경로로 보장되므로 [userId]가 곧 접근 범위다([myTimeline]과 같은 구조) —
     * 남의 uid로 부르면 거부가 아니라 빈 목록이다.
     */
    suspend fun personalEvents(
        userId: String,
        startAt: String,
        endAt: String,
    ): List<CalendarEvent>

    /** 학생이 수강 중인 반. 시간표 화면의 원본이다. */
    suspend fun studentSchedule(academyId: String, studentUserId: String): List<ClassData>

    suspend fun studentAssignments(academyId: String, studentUserId: String): List<Assignment>

    /** v4 대시보드 수업 테이블. `daysOfWeek` array-contains + `startMinuteOfDay` asc 인덱스(§S8). */
    suspend fun todayClasses(academyId: String, serviceDate: String?): List<TodayClass>

    /** v4 통계 4타일. */
    suspend fun attendanceSummary(academyId: String, serviceDate: String?): AttendanceSummary

    /**
     * 학원 출결 원장(파생 프로젝션). [classId]가 null이면 학원 전체다.
     *
     * 선생님은 배정 반으로 쿼리를 제약해야 통과한다(§4.5). `permission-denied`는
     * 실패가 아니라 **멤버십 재조회 신호**로 처리한다(§9.3-2, R7).
     */
    suspend fun dailyRoster(
        academyId: String,
        serviceDate: String?,
        classId: String?,
    ): List<DailyRosterEntry>

    /**
     * 기간 출결 원장. [dailyRoster]의 기간 확장판이며 통계 화면의 유일한 원본이다.
     *
     * 구간은 **양끝 포함**이고 `yyyy-MM-dd` 문자열 비교로 자른다(`serviceDate`가 학원
     * timeZone 기준으로 이미 정규화돼 있어 사전순 비교가 곧 날짜순 비교다, R12).
     *
     * [classId]가 null이면 학원 전체, [studentId]가 null이면 학생 전체다. 선생님은
     * 배정 반으로 [classId]를 제약해야 통과한다(§4.5) — [dailyRoster]와 같은 규칙이며
     * `permission-denied`도 똑같이 **멤버십 재조회 신호**로 다룬다.
     *
     * **스태프 전용이다.** rules가 `attendanceRecords` read를 ADMIN과 배정 반을 가진
     * EDITOR에게만 연다. 학생·보호자 화면은 이것 대신 [myTimeline]을 써야 한다.
     */
    suspend fun attendanceRecordsRange(
        academyId: String,
        startDate: String,
        endDate: String,
        classId: String?,
        studentId: String?,
    ): List<DailyRosterEntry>

    /**
     * 개인정보 미러 조회. `edubridge_users/{userId}/attendanceTimeline`(§4.3).
     *
     * 격리가 경로로 보장되므로 [userId]가 곧 접근 범위다. 학부모는 [subjectStudentId]로
     * 자녀를 고르고, null이면 관찰 가능한 전원이 섞여 온다.
     * [academyId]가 null이면 전 학원이다(자녀가 여러 학원에 다닐 수 있다).
     */
    suspend fun myTimeline(
        userId: String,
        academyId: String?,
        subjectStudentId: String?,
        limit: Int,
    ): List<AttendanceTimelineEntry>

    /** 학부모의 관찰 대상. ACTIVE 링크만이 관찰 범위다(§2.4). */
    suspend fun guardianLinks(guardianUserId: String): List<GuardianLink>

    /**
     * 한 학생에게 연결된 보호자 전원. 스태프의 학생 상세 화면이 쓴다.
     *
     * [guardianLinks]가 보호자 uid로 자르는 것과 방향이 반대다 — 이쪽은 rules가 스태프에게
     * read를 열어 준 학원 범위 조회이고, REVOKED 링크도 포함해 돌려준다(해지 이력 표시).
     */
    suspend fun studentGuardianLinks(academyId: String, studentId: String): List<GuardianLink>

    /**
     * 학원 단위 과제 목록. ACTIVE 멤버 전원이 읽는다.
     *
     * 공지·게시글과 같이 **툼스톤도 그대로 올라온다** — `status == ACTIVE` 필터는
     * `EduConnectRepositoryImpl`에서 끝난다.
     */
    suspend fun assignments(academyId: String): List<Assignment>

    /**
     * 한 과제의 제출 결과 전체. 스태프의 제출 현황 화면이 쓴다.
     *
     * rules가 스태프에게만 목록 조회를 열어 두므로(학생은 자기 행만) 일반 멤버가 부르면
     * 빈 목록이다. 기본 구현이 빈 목록인 이유는 아래 `joinApplications`와 같다 —
     * 테스트 페이크를 포함한 구현체가 한 커밋에서 다 같이 깨지지 않게 한다.
     */
    suspend fun assignmentSubmissions(
        academyId: String,
        assignmentId: String,
    ): List<AssignmentSubmission> = emptyList()

    /**
     * SUPER_ADMIN 승인 큐. 학원에 속하지 않은 플랫폼 범위 조회라 `academyId`를 받지 않는다.
     *
     * rules는 SUPER_ADMIN과 신청자 본인에게만 read를 연다 — 권한이 없으면 빈 목록이다.
     */
    suspend fun pendingAcademyApplications(): List<AcademyApplication>

    /**
     * 신청자 본인의 학원 개설 신청 이력. 학원 미소속(뷰어) 사용자가 자기 신청의 처리 상태를
     * 확인하는 경로다(S9-2·S9-3).
     *
     * [pendingAcademyApplications]와 **쿼리 축이 다르다**: 저쪽은 `status == PENDING`만 걸어
     * SUPER_ADMIN만 통과한다. rules가 문서마다 `applicantUid == uid`를 요구하므로
     * 일반 사용자는 반드시 `applicantUid`로 쿼리를 제약해야 하고, 그렇지 않으면 쿼리 전체가
     * 거부된다. 인덱스는 `(applicantUid, status)`가 이미 있다.
     */
    suspend fun myAcademyApplications(applicantUid: String): List<AcademyApplication>

    /**
     * 스태프 신고 대기열. **콜러블이 아니라 Firestore 직접 읽기다** —
     * `edubridge_academies/{academyId}/contentReports`를 rules가 스태프에게만 연다.
     *
     * 일반 멤버에게 이 컬렉션을 열지 않는 이유는 [ContentReport.reporterUid] 때문이다.
     * 누가 자신을 신고했는지 알 수 있으면 신고 자체가 위험해진다.
     *
     * [openOnly]가 참이면 `status == OPEN`만 온다. 대기열 화면의 기본값이며,
     * 거짓이면 종결된 것까지 포함해 처리 이력을 볼 수 있다. 기본 인자를 두지 않는 것은
     * 이 인터페이스의 규약이다(ObjC 헤더에 기본값이 노출되지 않는다).
     */
    suspend fun contentReports(academyId: String, openOnly: Boolean): List<ContentReport>

    /**
     * 스태프 가입 신청 큐(W7). [contentReports]와 동형의 **Firestore 직접 읽기다** —
     * `edubridge_academies/{academyId}/joinApplications`를 rules가 스태프에게만 연다.
     * 문서 id가 신청자 uid라 일반 멤버에게 열면 uid 열거가 되기 때문이다.
     *
     * [status]로 큐 필터를 건다(대기 화면 PENDING, 차단 관리 화면 BLOCKED) — 인덱스가
     * `(status, submittedAt desc)`뿐이라 상태 없는 전체 조회는 계약에 없다.
     * 정렬은 최근 제출 순(내림차순)으로 온다.
     *
     * 웹이 먼저 붙는다. 미구현 기기는 실패 규약과 같은 빈 목록 기본 구현을 상속한다 —
     * 앱이 UI를 붙일 때 abstract로 전환한다(`contentReports`가 밟은 길, a8e7a58).
     */
    suspend fun joinApplications(
        academyId: String,
        status: JoinApplicationStatus,
    ): List<JoinApplication> = emptyList()

    /**
     * 본인 가입 신청 미러(W7). `edubridge_users/{userId}/joinApplications` —
     * [myTimeline]과 같은 자리·같은 관용구이며 격리가 경로로 보장되므로 [userId]가 곧
     * 접근 범위다. 학원 수만큼만 문서가 생기므로 필터 없이 전부 돌려준다.
     *
     * 기본 구현이 빈 목록인 이유는 [joinApplications]와 같다.
     */
    suspend fun myJoinApplications(userId: String): List<JoinApplication> = emptyList()

    // ---- 실패를 구분하는 읽기 (#30, 단계 1) ----
    //
    // 위 메서드들은 복구 가능한 실패를 빈 목록으로 접는다(실패 규약). 그래서 화면이 "없음"과
    // "못 읽음"을 구분하지 못하고 같은 빈 상태를 그린다 — 사용자는 재시도할 방법도, 재시도해야
    // 한다는 사실도 알 수 없다.
    //
    // **기본 구현이 기존 메서드에 위임한다.** 그래서 이 블록이 추가돼도 어댑터 4종은 컴파일이
    // 깨지지 않고, 각 레인이 준비되면 자기 커밋으로 override 한다(`contentReports`가 밟은 길).
    // 기본 구현은 `failure = null`을 돌려주므로 **override 전에는 이 메서드도 실패를 구분하지
    // 못한다** — 위임일 뿐 마법이 아니다.
    //
    // 이전이 끝나면 위쪽 원본 메서드들은 삭제된다(제안서 §3.2 단계 4). 그 전까지 읽기 표면이
    // 일시적으로 두 배인 것은 이 경로의 알려진 비용이다.
    // 범위는 웹 UI가 직접 호출하는 14종 — 웹 레인 단독 선행(2026-08-15 결정)이라 나머지는
    // 그 레인이 착수할 때 같은 방식으로 늘린다.

    suspend fun academiesResult(academyIds: List<String>): ReadResult<AcademyData> =
        ReadResult.of(academies(academyIds))

    suspend fun classesResult(academyId: String): ReadResult<ClassData> =
        ReadResult.of(classes(academyId))

    suspend fun studentsResult(academyId: String): ReadResult<StudentData> =
        ReadResult.of(students(academyId))

    suspend fun noticesResult(academyId: String): ReadResult<NoticeData> =
        ReadResult.of(notices(academyId))

    suspend fun postsResult(academyId: String): ReadResult<PostData> =
        ReadResult.of(posts(academyId))

    /** 0개 또는 1개. "글 없음"은 `isEmptySuccess`, "못 읽음"은 `failure` — [post]의 null은 둘을 뭉갠다. */
    suspend fun postResult(academyId: String, postId: String): ReadResult<PostData> =
        ReadResult.of(listOfNotNull(post(academyId, postId)))

    suspend fun postCommentsResult(academyId: String, postId: String): ReadResult<PostComment> =
        ReadResult.of(postComments(academyId, postId))

    suspend fun studentScheduleResult(
        academyId: String,
        studentUserId: String,
    ): ReadResult<ClassData> = ReadResult.of(studentSchedule(academyId, studentUserId))

    suspend fun studentAssignmentsResult(
        academyId: String,
        studentUserId: String,
    ): ReadResult<Assignment> = ReadResult.of(studentAssignments(academyId, studentUserId))

    suspend fun assignmentsResult(academyId: String): ReadResult<Assignment> =
        ReadResult.of(assignments(academyId))

    suspend fun assignmentSubmissionsResult(
        academyId: String,
        assignmentId: String,
    ): ReadResult<AssignmentSubmission> =
        ReadResult.of(assignmentSubmissions(academyId, assignmentId))

    suspend fun pendingAcademyApplicationsResult(): ReadResult<AcademyApplication> =
        ReadResult.of(pendingAcademyApplications())

    suspend fun myAcademyApplicationsResult(applicantUid: String): ReadResult<AcademyApplication> =
        ReadResult.of(myAcademyApplications(applicantUid))

    suspend fun contentReportsResult(
        academyId: String,
        openOnly: Boolean,
    ): ReadResult<ContentReport> = ReadResult.of(contentReports(academyId, openOnly))

    suspend fun joinApplicationsResult(
        academyId: String,
        status: JoinApplicationStatus,
    ): ReadResult<JoinApplication> = ReadResult.of(joinApplications(academyId, status))

    suspend fun myJoinApplicationsResult(userId: String): ReadResult<JoinApplication> =
        ReadResult.of(myJoinApplications(userId))

    // 아래 3종은 안드로이드 레인이 착수하며 추가했다(2026-08-17) — 웹 화면은 쓰지 않는다.
    // 위 주석의 "나머지는 그 레인이 착수할 때 같은 방식으로 늘린다"가 이것이다.

    suspend fun todayClassesResult(
        academyId: String,
        serviceDate: String?,
    ): ReadResult<TodayClass> = ReadResult.of(todayClasses(academyId, serviceDate))

    suspend fun academyEventsResult(
        academyId: String,
        startAt: String,
        endAt: String,
    ): ReadResult<CalendarEvent> = ReadResult.of(academyEvents(academyId, startAt, endAt))

    suspend fun personalEventsResult(
        userId: String,
        startAt: String,
        endAt: String,
    ): ReadResult<CalendarEvent> = ReadResult.of(personalEvents(userId, startAt, endAt))

    /**
     * 실시간 출결 피드(v4 대시보드).
     *
     * **`Flow`가 아니라 콜백인 이유**: `Flow`는 ObjC 헤더로 브리징되지 않아 Swift가 이
     * 인터페이스를 구현할 수 없다. 기본 구현을 붙이는 우회도 통하지 않는다 — Kotlin 인터페이스의
     * 기본 구현은 생성된 ObjC 프로토콜로 넘어오지 않고 전부 required 멤버로 나가기 때문에,
     * `Flow` 메서드가 여기 남아 있는 한 iOS는 conformance를 선언조차 못 한다.
     *
     * 위쪽은 그대로 `Flow`다. [EduConnectRepository.attendanceEventStream]이 이 콜백을
     * `callbackFlow`로 되받으므로 repository·usecase·Android·웹 화면 코드는 바뀌지 않는다.
     *
     * [onComplete]는 구독이 스스로 끝났을 때 정확히 한 번 불린다(권한 상실, 쿼리 실패). 이것이
     * 없으면 Swift의 `AsyncStream`이 영원히 끝나지 않는다. 실패 사유는 싣지 않는다 — 읽기 실패는
     * 빈 결과라는 이 인터페이스의 규약과 갈라지기 때문이다.
     *
     * **호출자가 [Cancellable.cancel]로 끊은 경우 [onComplete]은 불리지 않는다.** 그때는 끊은
     * 쪽이 이미 알고 있다. 구현은 이 규칙을 지켜야 한다 — 취소 경로에서 [onComplete]을 부르면
     * 이미 닫히는 중인 수집기에 종료를 한 번 더 알리게 된다.
     *
     * 콜백은 이 함수가 반환하기 전에 **동기적으로 불릴 수 있다**(인메모리 구현이 그렇다). 반환된
     * 핸들을 콜백 안에서 참조하는 코드는 그 경우를 견뎌야 한다.
     *
     * Firebase 구현들은 콜백을 플랫폼 메인 디스패처에서 부른다 — JS의 `Dispatchers.Main`은
     * setTimeout 기반 매크로태스크 큐이고, datasource **생성 시점**에 해석된다(구독 시점이 아니다).
     *
     * ⚠️ 반환된 [Cancellable]을 버리면 **구독이 영원히 남는다.** 이전 `Flow` 계약에서는 수집기
     * 수명이 구독 수명이라 이런 상태를 표현할 수 없었지만, 이제는 핸들을 들고 있는 쪽이 수명을
     * 진다. Firestore 리스너가 살아 있는 동안 문서 읽기 과금이 계속된다.
     *
     * 기본 인자를 두지 않는 것은 이 인터페이스의 규약이다(ObjC 헤더에 기본값이 노출되지 않는다).
     */
    fun observeAttendanceEvents(
        academyId: String,
        limit: Int,
        onEach: (List<AttendanceEvent>) -> Unit,
        onComplete: () -> Unit,
    ): Cancellable
}
